import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { STORAGE_KEYS } from 'constants/storageKeys';
import authenticationService from 'services/authenticationService';
import { tenantStorage } from 'utils/tenantStorage';
import { CustomerProfile, LoginRequest, RegisterRequest } from 'types/auth';

interface AuthContextValue {
  isAuthenticated: boolean;
  customer: CustomerProfile | null;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  activateAccount: (token: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!tenantStorage.getItem(STORAGE_KEYS.TOKEN));
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);

  const refreshProfile = useCallback(async () => {
    if (!tenantStorage.getItem(STORAGE_KEYS.TOKEN)) {
      setCustomer(null);
      setIsAuthenticated(false);
      return;
    }
    try {
      const profile = await authenticationService.getProfile();
      setCustomer(profile);
      setIsAuthenticated(true);
    } catch {
      setCustomer(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refreshProfile();
    }
  }, [isAuthenticated, refreshProfile]);

  const login = async (data: LoginRequest) => {
    await authenticationService.login(data);
    setIsAuthenticated(true);
    await refreshProfile();
  };

  const register = async (data: RegisterRequest) => {
    await authenticationService.register(data);
    setIsAuthenticated(true);
    await refreshProfile();
  };

  const activateAccount = async (token: string, newPassword: string) => {
    await authenticationService.activateAccount({ token, newPassword });
    setIsAuthenticated(true);
    await refreshProfile();
  };

  const logout = async () => {
    await authenticationService.logout();
    setCustomer(null);
    setIsAuthenticated(false);
  };

  const value = useMemo(
    () => ({ isAuthenticated, customer, login, register, activateAccount, logout, refreshProfile }),
    [isAuthenticated, customer, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext debe usarse dentro de AuthProvider');
  return ctx;
};
